import { compileDashboardFeature, DashboardComponentType, Legend } from '@/features/dashboard'
import { EntityLevel } from '@/composables/useEntityLevel'
import { ColorScaleInterpolate } from '@/utils/colorScale'
import { formatCount, formatArea } from '@/features/analytics/formats'

const enabledEntityLevels: EntityLevel[] = ['COMPLEX', 'REAL_ESTATE']

const colorScale: ColorScaleInterpolate = {
  type: 'INTERPOLATE',
  cutoffs: [
    {
      color: '#E6F7FF',
      value: 0
    },
    {
      color: '#40A9FF',
      value: 250
    },
    {
      color: '#003A8C',
      value: 500
    }
  ]
}

const legend: Legend = {
  type: 'slider'
}

const format = formatCount

const formatVisitTime: FormatOptions = {
  format: {
    type: 'intl-number',
    maximumFractionDigits: 0
  },
  steps: [
    {
      format: {
        type: 'locale-format-string',
        format: {
          sv: '%s min',
          en: '%s min'
        }
      }
    }
  ]
}

export const visitorAnalysis = compileDashboardFeature({
  name: 'visitor-analysis',
  title: {
    sv: 'Besöks&shy;statistik',
    en: 'Visit statistics'
  },
  dataSectionTitle: {
    sv: 'Besöksstatistik',
    en: 'Visit statistics'
  },
  icon: 'IconUsers01',
  enabled: true,
  defaultFilters: {
    calendar: {
      quickSelect: 'last30days'
    },
    defaultDateSelection: 'last30d',
    onlyBusinessDays: true,
    onlyBusinessHours: true,
    onlyBusinessHoursFilterType: 'suffix',
    dateSelections: [
      'yesterday',
      'last7d',
      'last30d',
      'lastquarter',
      'last12m',
      'currentyear',
      'lastyear',
      'custom'
    ]
  },
  measurements: [
    {
      name: 'people_count_aggregates',
      field: {
        name: 'number_of_people_avg_avg',
        expression: {
          type: 'avg',
          exp: {
            type: 'field',
            name: 'number_of_people_avg'
          }
        }
      },
      title: {
        sv: 'Genomsnittligt antal personer',
        en: 'Average number of people'
      },
      options: {
        color: '#40A9FF',
        enabledEntityLevels,
        format,
        legend
      }
    },
    {
      name: 'people_count_aggregate_hour',
      field: {
        name: 'number_of_people_avg_avg',
        expression: {
          type: 'avg',
          exp: {
            type: 'field',
            name: 'total_people'
          }
        }
      },
      title: {
        sv: 'Genomsnittligt antal personer',
        en: 'Average number of people'
      },
      options: {
        color: '#40A9FF',
        enabledEntityLevels,
        colorScale,
        format,
        legend
      }
    },
    {
      name: 'people_count_sum_max',
      field: {
        name: 'number_of_people_avg_sum',
        expression: {
          type: 'max',
          exp: {
            type: 'field',
            name: 'total_people_sum'
          }
        }
      },
      title: {
        sv: 'Högst summa antal personer',
        en: 'Max sum number of people'
      },
      options: {
        color: '#FF7A45',
        enabledEntityLevels,
        colorScale,
        format,
        legend
      }
    },
    {
      name: 'people_count_aggregates',
      field: {
        name: 'visit_time_avg',
        expression: {
          type: 'avg',
          exp: {
            type: 'field',
            name: 'visit_time'
          }
        }
      },
      title: {
        sv: 'Genomsnittlig besökstid',
        en: 'Average visit time'
      },
      options: {
        color: '#FFC53D',
        enabledEntityLevels,
        format: formatVisitTime,
        legend
      }
    },
    {
      name: 'people_count_aggregates',
      // field: 'number_of_people_max_avg',
      field: {
        name: 'number_of_people_max_avg',
        expression: {
          type: 'avg',
          exp: {
            type: 'field',
            name: 'number_of_people_max'
          }
        }
      },
      title: {
        sv: 'Genomsnittligt högst antal personer',
        en: 'Average max number of people'
      },
      options: {
        color: '#F759AB',
        enabledEntityLevels,
        format,
        legend
      }
    },
    {
      name: 'people_count_aggregates',
      // field: 'visitors_avg',
      field: {
        name: 'visitors_avg',
        expression: {
          type: 'avg',
          exp: {
            type: 'field',
            name: 'visitors'
          }
        }
      },
      title: {
        sv: 'Genomsnittligt antal besök per dag',
        en: 'Average number of visits per day'
      },
      options: {
        color: '#36CFC9',
        enabledEntityLevels,
        format,
        legend
      }
    },
    {
      name: 'people_count_aggregates',
      // field: 'number_of_people_max_max',
      field: {
        name: 'number_of_people_max_max',
        expression: {
          type: 'max',
          exp: {
            type: 'field',
            name: 'number_of_people_max'
          }
        }
      },
      title: {
        sv: 'Högst antal personer',
        en: 'Max number of people'
      },
      options: {
        color: '#FF7A45',
        enabledEntityLevels,
        format,
        legend
      }
    },
    {
      name: 'people_count_aggregates',
      field: {
        name: 'area_per_person',
        expression: {
          type: 'avg',
          exp: {
            type: 'field',
            name: 'area_per_person'
          }
        }
      },
      title: {
        sv: 'm² per person',
        en: 'm² per person'
      },
      options: {
        color: '#9254DE',
        enabledEntityLevels,
        format: formatArea,
        legend
      }
    }
  ],
  components: [
    {
      type: DashboardComponentType.Kpi,
      name: 'average-number-of-people',
      measurement: 'people_count_aggregates.number_of_people_avg_avg',
      info: {
        sv: 'Visar hur många personer som i genomsnitt varit i byggnaden under valt tidsspann.',
        en: 'Shows the average number of people in the building during the selected time span.'
      }
    },
    {
      type: DashboardComponentType.Kpi,
      name: 'average-visit-time',
      measurement: 'people_count_aggregates.visit_time_avg',
      info: {
        sv: 'Visar hur länge personer stannar i genomsnitt. Genom att summera antalet personer över dagen och dividera med antalet besök får vi en uppfattning på hur länge personer stannar i genomsnitt.',
        en: 'Shows how long people stay on average. By summing the number of people over the day and dividing by the number of visits, we get an idea of how long people stay on average.'
      }
    },
    {
      type: DashboardComponentType.Kpi,
      name: 'average-max-number-of-people',
      measurement: 'people_count_aggregates.number_of_people_max_avg',
      info: {
        sv: 'Visar ett genomsnitt av det högsta antalet personer som samtidigt varit på ett campus eller i en byggnad under en timme för valt tidsspann \n\n \nExempel: Tidsspann en vecka.  \nVarje dags högst uppmätta antal personer är följande.  \n\n \n- Mån: 10 personer \n- Tis: 9 personer \n- Ons: 11 personer \n- Tors: 15 personer \n- Fredag: 10 personer \n\nDå ska detta mätvärde visa 11 personer. ((10+9+11+15+10)/5 =11)',
        en: 'Shows an average of the highest number of people who have been on a campus or in a building at the same time during an hour for the selected time span \n\nExample: Time span one week. \nThe highest measured number of people each day is as follows. \n\n- Mon: 10 people \n- Tue: 9 people \n- Wed: 11 people \n- Thu: 15 people \n- Fri: 10 people \n\nThen this measurement value should show 11 people. ((10+9+11+15+10)/5 =11)'
      }
    },
    {
      type: DashboardComponentType.Kpi,
      name: 'average-number-of-visits-per-day',
      measurement: 'people_count_aggregates.visitors_avg',
      info: {
        sv: 'Visar hur många besök ett campus en byggnad har i genomsnitt över valt tidsperiod. OBS! Detta är inte unika besökare utan ger en uppfattning om hur användarmönstret ser ut. Möjlighet för jämförelse mellan byggnader.',
        en: 'Shows how many visits a campus a building has on average over the selected time period. NOTE! These are not unique visitors but give an idea of what the user pattern looks like. Possibility for comparison between buildings.'
      }
    },
    {
      type: DashboardComponentType.Kpi,
      name: 'max-sum-number-of-people',
      measurement: 'people_count_sum_max.number_of_people_avg_sum',
      title: {
        sv: 'Högsta summa antal personer',
        en: 'Max sum number of people'
      },
      measurementOverride: {
        options: {
          color: '#FF7A45',
          enabledEntityLevels: []
        }
      },
      info: {
        sv: 'Visar det högsta antalet personer som samtidigt varit på ett campus eller i en byggnad under en timme för valt tidsspann \n\nExempel: Tidsspann en vecka. \nVarje dags högst uppmätta antal personer är följande.\n\n- Mån: 10 personer \n- Tis: 9 personer \n- Ons: 11 personer \n- Tors: 15 personer \n- Fredag: 10 personer \n\nDå ska detta mätvärde visa 15 personer.',
        en: 'Shows the highest number of people who have been on a campus or in a building at the same time during an hour for the selected time span \n\nExample: Time span one week. \nThe highest measured number of people each day is as follows.\n\n- Mon: 10 people \n- Tue: 9 people \n- Wed: 11 people \n- Thu: 15 people \nFriday: 10 people \n\nThen this measurement value should show 15 people.'
      }
    },
    {
      type: DashboardComponentType.Kpi,
      name: 'area-per-person',
      measurement: 'people_count_aggregates.area_per_person',
      info: {
        sv: 'Visar hur många kvadratmeter det går per person i genomsnitt. Möjliggör jämförelse mellan byggnader och campus.',
        en: 'Shows how many square meters there are per person on average. Enables comparison between buildings and campuses.'
      }
    },
    {
      type: DashboardComponentType.Kpi,
      name: 'max-number-of-people',
      measurement: 'people_count_aggregates.number_of_people_max_max',
      enabled: false,
      title: {
        sv: 'Högsta antal personer',
        en: 'Max number of people'
      },
      info: {
        en: 'Shows the highest number of people over a time period.',
        sv: 'Visar högsta antal personer under valt tidsintervall.'
      }
    },
    {
      type: DashboardComponentType.TimeSeries,
      name: 'average-number-of-people-time-series',
      measurements: [
        'people_count_aggregates.number_of_people_avg_avg',
        'people_count_sum_max.number_of_people_avg_sum',
        'people_count_aggregates.number_of_people_max_avg'
      ],
      title: {
        sv: 'Antal personer över tid',
        en: 'Number of people over time'
      },
      info: {
        sv: 'Grafen visar tre mätvärden för antal personer över tid. \n\n Mätvärden: \n\n - Genomsnittligt antal personer: Visar hur många personer som i genomsnitt varit i byggnaden under valt tidsspann \n - Genomsnittligt högst antal personer: Visar ett genomsnitt av det högsta antalet personer som samtidigt varit på ett campus eller i en byggnad under en timme för valt tidsspann \n - Högst summa antal personer: Visar det högsta antalet personer som samtidigt varit på ett campus eller i en byggnad under en timme för valt tidsspann ',
        en: 'The graph shows three measurements for the number of people over time. \n\n Measurements: \n\n - Average number of people: Shows the average number of people in the building during the selected time span \n - Average max number of people: Shows an average of the highest number of people who have been on a campus or in a building at the same time during an hour for the selected time span \n - Max sum number of people: Shows the highest number of people who have been on a campus or in a building at the same time during an hour for the selected time span '
      }
    },
    {
      type: DashboardComponentType.BarChart,
      name: 'average-number-of-people-bar-chart-dow',
      measurements: [
        'people_count_aggregates.number_of_people_avg_avg'
        // 'people_count_aggregates.number_of_people_max_avg'
      ],
      groupKey: 'timestamp_extract_isodow',
      filters: {
        values: {
          query: {
            groupBy: [{ type: 'timestamp_extract', extract: 'ISODOW' }]
          }
        }
      },
      title: {
        sv: 'Genomsnittligt antal personer över veckodagar',
        en: 'Average number of people over weekdays'
      },
      info: {
        sv: 'Stapeldiagrammet visar genomsnittligt antal personer aggregerat över veckodagar. \n\n Mätvärde: \n\n- Genomsnittligt antal personer: Visar hur många personer som i genomsnitt varit i byggnaden under valt tidsspann',
        en: 'The bar chart shows the average number of people aggregated over weekdays. \n\n Measurement: \n\n- Average number of people: Shows the average number of people in the building during the selected time span.'
      }
    },
    {
      type: DashboardComponentType.BarChart,
      name: 'average-number-of-people-bar-chart-hour',
      measurements: [
        'people_count_aggregate_hour.number_of_people_avg_avg'
        // 'people_count_aggregates.number_of_people_max_avg'
      ],
      groupKey: 'timestamp_extract_hour',
      measurementOverride: {
        options: {
          colorScale: null
        }
      },
      filters: {
        values: {
          query: {
            groupBy: [{ type: 'timestamp_extract', extract: 'HOUR' }]
          }
        }
      },
      title: {
        sv: 'Genomsnittligt antal personer över timmar',
        en: 'Average number of people over hours'
      },
      info: {
        sv: 'Stapeldiagrammet visar genomsnittligt antal personer aggregerat över timmar. \n\n Mätvärde: \n\n- Genomsnittligt antal personer: Visar hur många personer som i genomsnitt varit i byggnaden under valt tidsspann',
        en: 'The bar chart shows the average number of people aggregated over hours. \n\n Measurement: \n\n- Average number of people: Shows the average number of people in the building during the selected time span.'
      }
    },
    {
      type: DashboardComponentType.Heatmap,
      name: 'average-number-of-people-heatmap-hour',
      measurement: 'people_count_aggregate_hour.number_of_people_avg_avg',
      filters: {
        values: {
          query: {
            groupBy: [
              { type: 'timestamp_extract', extract: 'ISODOW' },
              { type: 'timestamp_extract', extract: 'HOUR' }
            ]
          }
        }
      },
      title: {
        sv: 'Genomsnittligt antal personer',
        en: 'Average number of people'
      },
      info: {
        sv: 'Färgdiagrammet visar genomsnittligt antal personer aggregerat över veckodag och timmar. \n\n Mätvärde: \n\n- Genomsnittligt antal personer: Visar hur många personer som i genomsnitt varit i byggnaden under valt tidsspann',
        en: 'The heatmap shows the average number of people aggregated over weekdays and hours. \n\n Measurement: \n\n- Average number of people: Shows the average number of people in the building during the selected time span.'
      }
    }
  ]
})
